import React, { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { IComboBoxOption } from "@fluentui/react";
import { SourceTypeEnum } from "../../../../api/models/source-type-enum";
import { fetchSheet, Sheet } from "../../../../api/object/Form";
import { fetchSheetExcelFile } from "../../../../api/object/ExcelFile";
import { retrieveDocumentProp } from "../../../../utils/helpers/CustomXmlHelper";
import { useAppSelector } from "../../../../redux/hooks/hooks";
import { selectCpmUsername } from "../../../../redux/feature/settings/configurations-slice";
import { useFilteredElaborations } from "../../../generic/GenericElaborationSelector";
import {
  ElaborationStatusItem,
  elaborationStatusItemIsAdded,
} from "../../../../redux/feature/object/remote-elaborations-slice";
import { DBApplication, FormTable, SheetForm } from "../../../interfaces/CoObject";

interface DropDownSheetState {
  sheetsItems: IComboBoxOption[];
  correlationId?: string;
  isOldValueForSheetToHide: boolean;
}

export interface DropDownSheetProps {
  isEdit?: boolean;
  sheet: SheetForm;
  form: FormTable;
  dbApplication: DBApplication;
  selectSheetComboBox: (key?: string, text?: string) => void;
  initAreas: (sheet: SheetForm) => void;
  type: string;
  objectSource: string;
  sheetsAreaChartMap: Map<string, string[]>;
  isFileExcelExisting?: boolean;
  isComponentInstance?: boolean;
  isLocked?: boolean;
  originalSheetArea?: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsOldValueForAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isOldValueForAreaToHide?: boolean;
  setHasSheetToBeSelected?: Dispatch<SetStateAction<boolean>>;
  isLoadingDataExcel?: boolean;
  setIsLoadingDataExcel?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  objectSourceImage?: string;
  isImageUnchecked?: boolean;
}

const useDropDownSheet = (props: DropDownSheetProps) => {
  const [state, setState] = React.useState<DropDownSheetState>({
    sheetsItems: [],
    isOldValueForSheetToHide: false,
  });
  const { sheetsItems, isOldValueForSheetToHide } = state;
  const {
    isEdit,
    type,
    sheetsAreaChartMap,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    originalSheetArea,
    setOriginalSheetArea,
    setIsOldValueForAreaToHide,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    setIsLoadingSheetAndArea,
  } = props;
  const username = useAppSelector(selectCpmUsername);

  function selectSheetComboBox(key?: string, text?: string) {
    props.selectSheetComboBox(key, text);
    if (props.objectSource === "EXCEL") {
      if (key === undefined) {
        return;
      }
      setOriginalSheetArea({ uid: key, desc: text, code: key });
      setState((s) => ({ ...s, isOldValueForSheetToHide: true }));
      props.setHasSheetToBeSelected(false);
      setIsOldValueForAreaToHide(true);
      props.setIsSheetChartAndAreaToHide(false);
    }
  }

  const sheetAndAreaElabFindFunction = useCallback(
    (item: ElaborationStatusItem) => {
      if (item && item.elabResult === "Error : Send failed") {
        props.setIsLoadingDataExcel(false);
        props.setIsLoadingSheetAndArea(false);
      }
      return (
        elaborationStatusItemIsAdded(item) &&
        item.elaborationType === "SHEET_AND_AREA" &&
        item.correlationId === state.correlationId
      );
    },
    [state]
  );

  const filteredElaborationItem = useFilteredElaborations({
    findFunction: sheetAndAreaElabFindFunction,
  });

  useEffect(() => {
    if (filteredElaborationItem && props.objectSource === SourceTypeEnum.EXCEL) {
      const sheets: Sheet[] = JSON.parse(filteredElaborationItem.elabResult);
      let foundSheet = sheets.find((value) => value.desc === props.sheet?.desc);
      if (!foundSheet && props.objectSourceImage?.includes("IMAGE")) {
        props.setIsSheetChartAndAreaToHide(true);
      } else {
        props.setIsSheetChartAndAreaToHide(false);
      }

      const storedSheetCode = originalSheetArea?.code;
      const storedSheetKey = storedSheetCode?.trim();
      const resolvedSheet = storedSheetKey
        ? sheets.find((sheet) => sheet.uid?.trim() === storedSheetKey || sheet.code === storedSheetCode)
        : undefined;
      const isStoredSheetUnresolved = Boolean(isEdit && storedSheetKey && !resolvedSheet);

      const resolvedSheetUid = resolvedSheet?.uid?.trim();
      if (
        originalSheetArea &&
        resolvedSheet &&
        resolvedSheetUid &&
        resolvedSheet.code === storedSheetCode &&
        resolvedSheetUid !== storedSheetCode
      ) {
        originalSheetArea.code = resolvedSheetUid;
        setOriginalSheetArea(originalSheetArea);
      }

      const items: IComboBoxOption[] = sheets.map((sheet) => {
        sheetsAreaChartMap.set(sheet.uid?.trim(), props.type === "CHART" ? sheet.charts : sheet.areas);
        return {
          key: sheet.uid?.trim(),
          text: sheet.desc,
        };
      });

      props.setIsOldSheetOrAreaSelected(isStoredSheetUnresolved);
      props.setHasSheetToBeSelected(isStoredSheetUnresolved);

      setState({
        ...state,
        sheetsItems: items,
        isOldValueForSheetToHide: !isStoredSheetUnresolved,
      });
      setIsOldValueForAreaToHide(!isStoredSheetUnresolved);
      props.setIsLoadingDataExcel(false);
      props.setIsLoadingSheetAndArea(false);
      props.initAreas(originalSheetArea);
    }
  }, [filteredElaborationItem?.elabResult, originalSheetArea?.code, originalSheetArea?.desc]);

  useEffect(() => {
    if (props.form != null && props.dbApplication != null && props.objectSource !== SourceTypeEnum.EXCEL) {
      fetchSheet(props.dbApplication.id, props.form.code, type).then((sheets) => {
        const items: IComboBoxOption[] = sheets.map((sheet) => {
          sheetsAreaChartMap.set(sheet.code, type == "CHART" ? sheet.charts : sheet.areas);
          return {
            key: sheet.code,
            text: sheet.desc,
          };
        });
        setState({ ...state, sheetsItems: items });
        props.setIsLoadingDataExcel(false);
        props.setIsLoadingSheetAndArea(false);
        props.initAreas(props.sheet);
      });
    } else if (props.form != null && props.objectSource === SourceTypeEnum.EXCEL) {
      if (isFileExcelExisting) {
        setState({ ...state, sheetsItems: [] });
        props.setIsLoadingDataExcel(true);
        props.setIsLoadingSheetAndArea(true);
        console.log("DropDownSheetHook ....");
        retrieveDocumentProp("Document-ID").then((documentId) => {
          fetchSheetExcelFile(username, documentId, props.form.code, props.type).then((response) =>
            setState((s) => ({ ...s, correlationId: response.correlationId }))
          );
        });
      }
    }
  }, [props.form]);

  return {
    isEdit,
    sheetsItems,
    selectSheetComboBox,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    originalSheetArea,
    setOriginalSheetArea,
    isOldValueForSheetToHide,
    setIsOldValueForAreaToHide,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    setIsLoadingSheetAndArea,
  };
};

export default useDropDownSheet;
